import React, { Profiler, useCallback, useContext, useEffect, useMemo } from 'react';
import { unstable_NormalPriority as NormalPriority, unstable_scheduleCallback as scheduleCallback } from 'scheduler';
import coinflip from '@atlassian/ufo-coinflip';
import { getInteractionRate, getConfig } from '@atlassian/ufo-config';
import UFOInteractionContext from '@atlassian/ufo-interaction-context';
import UFOInteractionIDContext from '@atlassian/ufo-interaction-id-context';
import { abortByNewInteraction, addApdex, addCustomData, addCustomTiming, addHold, addHoldByID, addMark, addNewInteraction, addProfilerTimings, addRequestInfo, addSegment, addSpan, removeHoldByID, removeSegment, tryComplete } from '@atlassian/ufo-interaction-metrics';
import UFORouteName from '@atlassian/ufo-route-name-context';
import generateId from '@atlassian/ufo-short-id';
let tryCompleteHandle;

/** A portion of the page we apply measurement to */
export default function UFOSegment({
  name: segmentName,
  children
}) {
  const parentContext = useContext(UFOInteractionContext);
  const segmentId = useMemo(() => generateId(), []);
  const labelStack = useMemo(() => parentContext !== null && parentContext !== void 0 && parentContext.labelStack ? [...parentContext.labelStack, {
    name: segmentName,
    segmentId
  }] : [{
    name: segmentName,
    segmentId
  }], [parentContext, segmentName, segmentId]);
  const interactionId = useContext(UFOInteractionIDContext);
  const interactionContext = useMemo(() => {
    addSegment(labelStack);
    let lastCompleteEndTime = 0;
    function complete(endTime = performance.now()) {
      if (interactionId.current) {
        if (parentContext) {
          parentContext.complete();
        } else {
          const capturedInteractionId = interactionId.current;
          if (endTime > lastCompleteEndTime) {
            lastCompleteEndTime = endTime;
          }
          if (tryCompleteHandle) {
            var _cancelAnimationFrame;
            (_cancelAnimationFrame = cancelAnimationFrame) === null || _cancelAnimationFrame === void 0 ? void 0 : _cancelAnimationFrame(tryCompleteHandle);
          }
          scheduleCallback(NormalPriority, () => {
            if (capturedInteractionId === interactionId.current) {
              var _document, _getConfig$enableComp, _getConfig;
              const isPageVisible = ((_document = document) === null || _document === void 0 ? void 0 : _document.visibilityState) === 'visible';
              const canDoRAF = typeof requestAnimationFrame !== 'undefined';
              const enableCompleteUsingDoubleRAF = (_getConfig$enableComp = (_getConfig = getConfig()) === null || _getConfig === void 0 ? void 0 : _getConfig.enableCompleteUsingDoubleRAF) !== null && _getConfig$enableComp !== void 0 ? _getConfig$enableComp : false;
              if (enableCompleteUsingDoubleRAF && isPageVisible && canDoRAF) {
                tryCompleteHandle = requestAnimationFrame(() => {
                  tryCompleteHandle = requestAnimationFrame(() => {
                    if (capturedInteractionId === interactionId.current) {
                      tryComplete(interactionId.current, lastCompleteEndTime);
                    }
                  });
                });
              } else {
                tryComplete(interactionId.current, lastCompleteEndTime);
              }
            }
          });
        }
      }
    }
    function _internalHold(labelStack, name
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    ) {
      if (interactionId.current != null) {
        if (parentContext) {
          return parentContext._internalHold(labelStack, name);
        } else {
          const capturedInteractionId = interactionId.current;
          const disposeHold = addHold(interactionId.current, labelStack, name);
          return () => {
            if (capturedInteractionId === interactionId.current) {
              disposeHold();
            }
          };
        }
      }
    }
    function _internalHoldByID(labelStack, id, name, remove
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    ) {
      if (interactionId.current != null) {
        if (parentContext) {
          parentContext._internalHoldByID(labelStack, name, id, remove);
          return;
        }
        if (!remove) {
          addHoldByID(interactionId.current, labelStack, name, id);
        } else {
          removeHoldByID(interactionId.current, id);
        }
      }
    }
    if (parentContext) {
      return {
        ...parentContext,
        labelStack,
        complete
      };
    }
    return {
      labelStack,
      hold(name = 'unknown'
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      ) {
        return this._internalHold(this.labelStack, name);
      },
      addHoldByID(labelStack, id, name = 'unknown') {
        this._internalHoldByID(labelStack, id, name, false);
      },
      removeHoldByID(labelStack, id, name = 'unknown') {
        this._internalHoldByID(labelStack, id, name, true);
      },
      tracePress(name = 'unknown', timestamp) {
        if (interactionId.current != null) {
          abortByNewInteraction(interactionId.current, name);
        }
        const rate = getInteractionRate(name, 'press');
        if (coinflip(rate)) {
          const startTimestamp = timestamp !== null && timestamp !== void 0 ? timestamp : performance.now();
          const newId = generateId();
          interactionId.current = newId;
          addNewInteraction(newId, name, 'press', startTimestamp, rate, this.labelStack, UFORouteName.current);
        }
      },
      retainQuery(info) {
        if (interactionId.current != null) {
          addRequestInfo(interactionId.current, this.labelStack, info);
        }
      },
      addPreload(moduleId, timestamp) {
        if (interactionId.current != null) {
          addMark(interactionId.current, 'bundle_preload', moduleId, this.labelStack, timestamp);
        }
      },
      addLoad(identifier, start, end) {
        if (interactionId.current != null) {
          const {
            labelStack = []
          } = this;
          addSpan(interactionId.current, 'bundle_load', identifier, labelStack, start, end);
        }
      },
      addMark(name, timestamp
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      ) {
        if (interactionId.current != null) {
          const time = timestamp !== null && timestamp !== void 0 ? timestamp : performance.now();
          addMark(interactionId.current, 'custom', name, this.labelStack, time);
        }
      },
      addCustomData(customData) {
        if (interactionId.current != null) {
          addCustomData(interactionId.current, this.labelStack, customData);
        }
      },
      addCustomTimings(customTimings) {
        if (interactionId.current != null) {
          addCustomTiming(interactionId.current, this.labelStack, customTimings);
        }
      },
      addApdex(apdexInfo) {
        if (interactionId.current != null) {
          const {
            key,
            stopTime,
            startTime
          } = apdexInfo;
          addApdex(interactionId.current, {
            key,
            stopTime,
            startTime,
            labelStack: this.labelStack
          });
        }
      },
      onRender(phase, actualDuration, baseDuration, startTime, commitTime) {
        if (interactionId.current !== null) {
          addProfilerTimings(interactionId.current, this.labelStack, phase, actualDuration, baseDuration, startTime, commitTime);
          this.complete(commitTime);
        }
      },
      _internalHold,
      _internalHoldByID,
      complete
    };
  }, [parentContext, labelStack, interactionId]);
  const onRender = useCallback((_id, phase, actualDuration, baseDuration, startTime, commitTime) => {
    interactionContext.onRender(phase, actualDuration, baseDuration, startTime, commitTime);
  }, [interactionContext]);
  useEffect(() => () => {
    removeSegment(labelStack);
  }, [interactionId, parentContext, interactionContext, labelStack]);
  const reactProfilerId = useMemo(() => labelStack.map(l => l.name).join('/'), [labelStack]);
  return /*#__PURE__*/React.createElement(UFOInteractionContext.Provider, {
    value: interactionContext
  }, /*#__PURE__*/React.createElement(Profiler, {
    id: reactProfilerId,
    onRender: onRender
  }, children));
}